import { Position } from '../../Geo'
import { ValidBusinessObject } from '../../datastoreProxy'
import { DeviceTypes } from '../DeviceTypes'
import { DeviceStatus, NWMonitoringPayloadAttributes } from '../MetaTypes'

export enum LinkType {
  UP = 'UP',
  DOWN = 'DOWN',
}

/**
 * Network independet common attributes from rxInfo / rx_metadata
 */
export type IOTV_LCD_RX_Data = {
  gatewayEui: string
  gatewayIdName: string | undefined
  gatewayLocation: Position | undefined

  antenna: number | undefined
  channel: number | undefined
  channel_rssi: number | undefined

  rfChain: number | undefined
  rssi: number | undefined
  snr: number | undefined
  esp?: number
}

/**
 * Network independent common attributes used in all LCD Message types emitted by NetworkData lambda
 */
export type IOTV_LCD_AS_BASE = {
  linkType: LinkType
  timestamp: number
  app_id: string
  nasSourceIp?: string
  nwID: string // Networks
  /** the unique identifier for the device */
  deviceEui: string
  /** a common attribute name as a proxy for device_id in TTN, deviceId in Chirpstack */
  deviceName: string | undefined
  correlation_ids: string[] | undefined
}

/**
 * Network independent common attributes used for gateway data and device messages emitted by NetworkData lambda
 */
export type IOTV_LCD_NetworkAndDeviceData = IOTV_LCD_AS_BASE & {
  consumedAirtimeSecs: number | undefined
  fCnt?: number
  fCntInc?: number
  port: number | undefined
  dr: number | undefined
  adr: boolean | undefined
  uplinkId: string | undefined

  deviceLocation?: Position | undefined

  sf: number | undefined
  bw: number | undefined
  codeRate: string | undefined
  frequency: number | undefined
  modulation: string | undefined
  power: number | undefined
}

export type MinimalBusinessObject<T extends string = string> = {
  type: T
  id: string
  name?: string
}

export type MinimalDeviceObject = MinimalBusinessObject & {
  eui: string
  deviceId: string | undefined
  deviceType: DeviceTypes
  networkKey: string
  appID: string
}

export type IoTV_LCD_DeviceRecord = MinimalDeviceObject & {
  dailyFCntSkip: number
  dailyMessageCount: number
  yesterdaysFCntJumpTotal: number | undefined
  yesterdaysMessageCntTotal: number | undefined
}

export type Bearings = {
  /** Angle from gateway to device from North ∠NGB (0-359º) */
  gatewayToDeviceBearing?: number
  /** Angle direction of device from North (0-359º) */
  deviceBearing?: number
  /** Angle between device direction to gateway (0-180º) */
  deviceToGatewayAngle?: number
}

export const deviceToMinimalBO = (item: ValidBusinessObject): MinimalDeviceObject => {
  const { id, type, name, eui, deviceId, deviceType, networkKey, appID } = item
  return { id, type, name, eui, deviceId, deviceType, networkKey, appID }
}

export const deviceToIoTVLCDDeviceRecord = (item: ValidBusinessObject): IoTV_LCD_DeviceRecord => {
  const { dailyFCntSkip, dailyMessageCount, yesterdaysFCntJumpTotal, yesterdaysMessageCntTotal } = item
  return { ...deviceToMinimalBO(item), dailyFCntSkip, dailyMessageCount, yesterdaysFCntJumpTotal, yesterdaysMessageCntTotal }
}

export const vobToMinimalBO = (item: ValidBusinessObject): MinimalBusinessObject => {
  const { id, type, name } = item
  return { id, type, name }
}
export const minmalBOToVob = (item: MinimalBusinessObject): ValidBusinessObject => {
  const { id, type, name } = item
  const key = `${type}:${id}`
  return { sk: key, pk: key, id, type, name }
}

/**
 * Network independent common attributes used for device messages emitted by NetworkData lambda
 */
export type IOTV_LCD_DeviceMessage = IOTV_LCD_NetworkAndDeviceData & {
  joinEui: string | undefined
  payloadStr: string | undefined
  primaryGatewayRXData?: IOTV_LCD_RX_Data
  secondaryGatewayRXData: IOTV_LCD_RX_Data[]
  nmMonitoringAttributes: NWMonitoringPayloadAttributes
  resolvedEntity?: MinimalBusinessObject
  resolvedDevice?: IoTV_LCD_DeviceRecord
  resolvedCustomer?: MinimalBusinessObject
  resolvedGroup?: MinimalBusinessObject
  resolvedProperty?: MinimalBusinessObject
  deviceType?: DeviceTypes
  bearings?: Bearings
  deviceId: string | undefined
}

/**
 * Network independent common attributes used for device messages emitted by NetworkData lambda
 */
export type IOTV_LCD_GatewayData = IOTV_LCD_NetworkAndDeviceData &
  IOTV_LCD_RX_Data & {
    isPrimaryGateway: boolean
  }

/**
 * Network independent common attributes used for application server downlink responses emitted by NetworkData lambda
 */
export type IOTV_LCD_AS_DOWNLINK_RESPONSE = IOTV_LCD_AS_BASE & {
  /**
   * nominally the repeated downlink request payload, only see so far on TTNE
   */
  payloadStr: string | undefined
  fCnt: number
}

/**
 *  common attributes used for original display of gateway data and device messages
 */
export type IOTV_LCD_MessageType = {
  customerID: string
  nwID: string
  gwEUI: string
  gwLong?: number
  gwLat?: number
  gwStatus: DeviceStatus | undefined
  gwLastSeen?: number
  devID: string
  devEUI: string
  devStatus: DeviceStatus | undefined
  devLastSeen?: number
  devGroup?: string
  devBGroup?: string
  devType?: string
  devLat?: number
  devLong?: number
  devBattery?: number
  devMessagePeriod?: number
  devBatteryCharging?: boolean
  devReboot?: boolean
  devDownlinkAck?: boolean
  devBbatteryErr?: boolean
  devLinkCheckErr?: boolean
  devJoinCount?: number
  DevMessUpCount?: number
  DevMessDownCount?: number
  recpPort: number
  recpChannel: number
  mRSSI: number
  mSNR: number
  ESP?: number
  recpDist?: number
  devGateways: number
  messTime: number
  mType: string
  messSF?: number
  messChain?: number
  mPayloadSize: number
}

export const getPartialLCDMessageRecord = (): Partial<IOTV_LCD_MessageType> => {
  return {
    customerID: undefined,
    nwID: undefined,
    gwEUI: undefined,
    gwLong: undefined,
    gwLat: undefined,
    gwStatus: undefined,
    gwLastSeen: undefined,
    devID: undefined,
    devEUI: undefined,
    devStatus: undefined,
    devLastSeen: undefined,
    devGroup: undefined,
    devBGroup: undefined,
    devType: undefined,
    devLat: undefined,
    devLong: undefined,
    devBattery: undefined,
    devMessagePeriod: undefined,
    devBatteryCharging: undefined,
    devReboot: undefined,
    devDownlinkAck: undefined,
    devBbatteryErr: undefined,
    devLinkCheckErr: undefined,
    devJoinCount: undefined,
    DevMessUpCount: undefined,
    DevMessDownCount: undefined,
    recpPort: undefined,
    recpChannel: undefined,
    mRSSI: undefined,
    mSNR: undefined,
    ESP: undefined,
    recpDist: undefined,
    devGateways: undefined,
    messTime: undefined,
    mType: undefined,
    messSF: undefined,
    messChain: undefined,
    mPayloadSize: undefined,
  }
}

/** the types added by NetworkData to device instance */
export type AdditionalNWDeviceAttributes = {
  app_id: string
  deviceEui: string
  nwID: string
  fCnt: number | undefined
  adr: boolean | undefined
  dr: number | undefined
  bw: number | undefined
  sf: number | undefined
  rfChain: number | undefined
  rssi: number | undefined
  snr: number | undefined
  channel: number | undefined
}
